import { Modal } from 'react-bootstrap';
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose';
import { EmptySearchResults } from 'Components/molecules';
import { IntegrationsFilter } from 'Components/molecules';
import { SearchBar } from 'Components/molecules';
import classNames from 'classnames';
import { useState, useMemo, useEffect } from 'react';
import { VendorCard } from 'Components/molecules';
import { IntegratedCard } from 'Components/molecules';
import AmplifierLogo from 'Assets/icons/amplifierLogoSymbol.svg';
import MicrosoftLogo from 'Assets/icons/microsoftLogoSymbol.svg';
import { AMPLIFIER, API_CALL, CUSTOM_ACTION, MICROSOFT } from 'Core-utils/constants/constants';
import { CustomActionDto, ServiceProfile } from '@ampsec/platform-client';
import {
    AMPLIFIER_GROUP_CARD_ID,
    AmplifierTrainingTemplateDefinition,
    buildAmplifierGroupCardStub,
    buildGenericCustomActionStub,
    getAmplifierTrainingTemplateById,
    isAmplifierTrainingTemplateId,
} from 'Components/organisms/CustomActionWizard/amplifierTraining/amplifierTrainingTemplates';
import { useAmplifierTrainingTemplates } from 'Components/organisms/CustomActionWizard/amplifierTraining/useAmplifierTrainingTemplates';
import {
    MICROSOFT_GROUP_CARD_ID,
    MICROSOFT_INTUNE_REBOOT_TITLE,
    buildMicrosoftGroupCardStub,
    buildMicrosoftIntuneRebootActionStub,
    isMicrosoftIntuneRebootActionId,
} from 'Components/organisms/CustomActionWizard/microsoftIntune/microsoftIntuneTemplates';
import { getServiceInfoByKey, resolveCustomActionServiceProfile } from 'Core-utils/helpers/helpers';
import { IntegrationFilterDataProps } from 'Components/molecules/IntegrationsFilter/IntegrationsFilter';
import { IntegrationDataProps } from 'Components/organisms/Integrations/Integrations';
import './styles.scss';

export type failedRunsType = {
    id: string;
    name: string;
    actionTarget: string;
    actionName: string;
    status: number;
    message: string;
};
export type CustomActionsModalType = Pick<
    IntegrationDataProps,
    'id' | 'imageUrl' | 'vendor' | 'tileDescription' | 'actionType'
> & {
    properties?: {
        url: string;
        payload: string;
    };
    headers?: {
        key: string;
        value: string;
    }[];
    parameters?: {
        key: string;
        value: string;
    }[];
    triggeredInLastMonth?: number;
    failedRuns?: failedRunsType[];
};

export interface CustomActionsModalProps {
    data: CustomActionDto[];
    show: boolean;
    onClose: () => void;
    onCardClick: (id: string) => void;
    variant: 'all' | 'selected';
    query: string;
    onSearchQueryChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    title: string;
    serviceProfileData: ServiceProfile[];
}

export interface ActionsIntegrationGridProps {
    data: CustomActionDto[];
    variant?: 'all' | 'selected';
    handleCardClick: (_id: string) => void;
    onAmplifierGroupClick?: () => void;
    onMicrosoftGroupClick?: () => void;
    serviceProfileData: ServiceProfile[];
    trainingTemplateDefinitions: AmplifierTrainingTemplateDefinition[];
}

const IntegrationsGrid = ({
    data,
    variant,
    handleCardClick,
    onAmplifierGroupClick,
    onMicrosoftGroupClick,
    serviceProfileData,
    trainingTemplateDefinitions,
}: ActionsIntegrationGridProps) => {
    const amplifierServiceProfile = getServiceInfoByKey(serviceProfileData, AMPLIFIER);
    const amplifierLogo = amplifierServiceProfile?.logo || AmplifierLogo;
    const microsoftServiceProfile = getServiceInfoByKey(serviceProfileData, MICROSOFT);
    const microsoftLogo = microsoftServiceProfile?.logo || MicrosoftLogo;

    const getAmplifierActionLabel = (template?: AmplifierTrainingTemplateDefinition): string => {
        switch (template?.key) {
            case 'scheduled':
                return 'SCHEDULED ACTION';
            case 'module':
                return 'SINGLE MODULE';
            case 'custom':
                return 'MULTIPLE MODULES';
            default:
                return AMPLIFIER.toUpperCase();
        }
    };

    const gridColClassName = classNames({
        'col-4': variant === 'all',
        'col-3': variant === 'selected',
    });
    const maxRowElements = variant === 'all' ? 3 : 4;

    const rows: JSX.Element[] = data.reduce((result: JSX.Element[]) => {
        const index = result.length * maxRowElements;

        const rowItems = data.slice(index, index + maxRowElements).map((item) => {
            const serviceProfile = resolveCustomActionServiceProfile(item, serviceProfileData);
            const trainingTemplate = isAmplifierTrainingTemplateId(item.id)
                ? getAmplifierTrainingTemplateById(item.id, trainingTemplateDefinitions)
                : undefined;

            return (
                <div
                    className={gridColClassName}
                    key={item.id || 'custom-action-template'}
                    data-testid={`card-${index}`}
                >
                    {item.id === AMPLIFIER_GROUP_CARD_ID ? (
                        <VendorCard
                            imageUrl={amplifierLogo}
                            title={AMPLIFIER}
                            subText={item.description ?? ''}
                            isIntegrated={false}
                            handleCard={() => onAmplifierGroupClick?.()}
                            id={item.id}
                        />
                    ) : item.id === MICROSOFT_GROUP_CARD_ID ? (
                        <VendorCard
                            imageUrl={microsoftLogo}
                            title={MICROSOFT}
                            subText={item.description ?? ''}
                            isIntegrated={false}
                            handleCard={() => onMicrosoftGroupClick?.()}
                            id={item.id}
                        />
                    ) : isMicrosoftIntuneRebootActionId(item.id) ? (
                        <VendorCard
                            imageUrl={microsoftLogo}
                            title={MICROSOFT_INTUNE_REBOOT_TITLE.toUpperCase()}
                            subTitle={MICROSOFT}
                            subText={item.description ?? ''}
                            isIntegrated={false}
                            handleCard={() => handleCardClick(item.id)}
                            id={item.id}
                        />
                    ) : trainingTemplate ? (
                        <VendorCard
                            imageUrl={amplifierLogo}
                            title={getAmplifierActionLabel(trainingTemplate)}
                            subTitle={AMPLIFIER}
                            subText={trainingTemplate.description}
                            isIntegrated={false}
                            handleCard={() => handleCardClick(item.id)}
                            id={item.id}
                        />
                    ) : item.displayValue === AMPLIFIER ? (
                        <IntegratedCard
                            vendor={API_CALL}
                            imageUrl={AmplifierLogo}
                            title={CUSTOM_ACTION.toUpperCase()}
                            handleCard={() => handleCardClick('')}
                            variant={'custom'}
                        />
                    ) : item.displayValue === CUSTOM_ACTION && item.id === '' ? (
                        <IntegratedCard
                            vendor={API_CALL}
                            imageUrl={AmplifierLogo}
                            title={CUSTOM_ACTION.toUpperCase()}
                            handleCard={() => handleCardClick('')}
                            variant={'custom'}
                        />
                    ) : (
                        <VendorCard
                            imageUrl={serviceProfile?.logo ?? ''}
                            title={serviceProfile?.displayValue ?? ''}
                            subText={item.description ?? ''}
                            isIntegrated={false}
                            handleCard={() => handleCardClick(item.id)}
                            id={item.id}
                        />
                    )}
                </div>
            );
        });

        result.push(
            <div className="row me-0" key={index}>
                {rowItems}
            </div>,
        );

        return result;
    }, []);

    const gridClassName = classNames('integrations-grid d-grid gap-3 w-100 overflow-auto custom-scrollbar', {
        'integrations-grid--stacked-cards': variant === 'all',
    });

    return <div className={gridClassName}>{rows}</div>;
};

const ALL_VIEW_SYNTHETIC_CARD_COUNT = 3;

const getFiltersData = (
    data: CustomActionDto[],
    serviceProfileData: ServiceProfile[],
    amplifierTemplateCount: number,
    microsoftActionCount: number,
): IntegrationFilterDataProps[] => {
    const vendorCounts: { [vendor: string]: { count: number; image?: string } } = {};

    (Array.isArray(data) ? data : []).forEach((item: CustomActionDto) => {
        if (
            item.id === AMPLIFIER_GROUP_CARD_ID ||
            isAmplifierTrainingTemplateId(item.id) ||
            item.id === MICROSOFT_GROUP_CARD_ID ||
            isMicrosoftIntuneRebootActionId(item.id)
        ) {
            return;
        }

        const serviceProfile = resolveCustomActionServiceProfile(item, serviceProfileData);
        const vendor = serviceProfile?.displayValue;
        if (!vendor || vendor === AMPLIFIER) {
            return;
        }

        if (vendorCounts[vendor]) {
            vendorCounts[vendor].count += 1;
        } else {
            vendorCounts[vendor] = { count: 1, image: serviceProfile?.logo };
        }
    });

    const amplifierServiceProfile = getServiceInfoByKey(serviceProfileData, AMPLIFIER);
    const amplifierLogo = amplifierServiceProfile?.logo || AmplifierLogo;
    const microsoftServiceProfile = getServiceInfoByKey(serviceProfileData, MICROSOFT);
    const microsoftLogo = microsoftServiceProfile?.logo || MicrosoftLogo;
    const apiDataLength = Array.isArray(data) ? data.length : 0;

    const filters: IntegrationFilterDataProps[] = [
        { text: 'All', count: apiDataLength + ALL_VIEW_SYNTHETIC_CARD_COUNT },
        {
            text: AMPLIFIER,
            count: amplifierTemplateCount,
            image: amplifierLogo,
        },
        {
            text: MICROSOFT,
            count: microsoftActionCount,
            image: microsoftLogo,
        },
    ];

    Object.keys(vendorCounts).forEach((key) => {
        filters.push({
            text: key,
            count: vendorCounts[key].count,
            image: vendorCounts[key].image,
        });
    });

    return filters;
};

/** Provider templates from the API — excludes synthetic picker cards and the API generic custom-action row. */
const getProviderTemplatesForAllView = (apiData: CustomActionDto[]): CustomActionDto[] =>
    apiData.filter(
        (item) =>
            item.id !== AMPLIFIER_GROUP_CARD_ID &&
            !isAmplifierTrainingTemplateId(item.id) &&
            item.displayValue !== AMPLIFIER &&
            item.id !== MICROSOFT_GROUP_CARD_ID &&
            !isMicrosoftIntuneRebootActionId(item.id) &&
            item.displayValue !== MICROSOFT,
    );

const buildAllViewItems = (
    apiData: CustomActionDto[],
    genericCustomAction: CustomActionDto,
    amplifierGroupCard: CustomActionDto,
    microsoftGroupCard: CustomActionDto,
): CustomActionDto[] => [
    genericCustomAction,
    amplifierGroupCard,
    microsoftGroupCard,
    ...getProviderTemplatesForAllView(apiData),
];

const CustomActionsModal = ({
    data,
    show,
    onClose,
    onCardClick,
    onSearchQueryChange,
    variant,
    query,
    title,
    serviceProfileData,
}: CustomActionsModalProps) => {
    const { stubs: amplifierTrainingTemplates, templates: trainingTemplateDefinitions } =
        useAmplifierTrainingTemplates();
    const [filter, setFilter] = useState('All');
    const genericCustomAction = useMemo(() => buildGenericCustomActionStub(), []);
    const amplifierGroupCard = useMemo(() => buildAmplifierGroupCardStub(), []);
    const microsoftGroupCard = useMemo(() => buildMicrosoftGroupCardStub(), []);
    const microsoftIntuneActions = useMemo(() => [buildMicrosoftIntuneRebootActionStub()], []);
    const apiData = useMemo(() => (Array.isArray(data) ? [...data] : []), [data]);

    useEffect(() => {
        if (!show) {
            setFilter('All');
        }
    }, [show]);

    const matchesSearch = (items: CustomActionDto[]) => {
        if (!query) {
            return items;
        }

        const searchString = query.trim().toLocaleLowerCase();
        return items.filter((ele) => {
            const haystack = `${ele?.description ?? ''} ${ele?.displayValue ?? ''}`.toLocaleLowerCase();
            const trainingTitle = isAmplifierTrainingTemplateId(ele.id)
                ? (getAmplifierTrainingTemplateById(ele.id, trainingTemplateDefinitions)?.title ?? '')
                : '';
            return `${haystack} ${trainingTitle.toLocaleLowerCase()}`.includes(searchString);
        });
    };

    const filteredData = useMemo(() => {
        if (variant === 'selected') {
            return matchesSearch([
                genericCustomAction,
                ...amplifierTrainingTemplates,
                ...microsoftIntuneActions,
                ...apiData,
            ]);
        }

        if (filter === AMPLIFIER) {
            return matchesSearch([...amplifierTrainingTemplates]);
        }

        if (filter === MICROSOFT) {
            return matchesSearch([...microsoftIntuneActions]);
        }

        if (filter === 'All') {
            return matchesSearch(
                buildAllViewItems(apiData, genericCustomAction, amplifierGroupCard, microsoftGroupCard),
            );
        }

        const vendorFiltered = apiData.filter((ele) => {
            const serviceProfile = resolveCustomActionServiceProfile(ele, serviceProfileData);
            return serviceProfile?.displayValue === filter;
        });

        return matchesSearch(vendorFiltered);
    }, [
        query,
        filter,
        apiData,
        variant,
        amplifierTrainingTemplates,
        microsoftIntuneActions,
        trainingTemplateDefinitions,
        genericCustomAction,
        amplifierGroupCard,
        microsoftGroupCard,
        serviceProfileData,
    ]);

    const filters = getFiltersData(
        apiData,
        serviceProfileData,
        amplifierTrainingTemplates.length,
        microsoftIntuneActions.length,
    );
    const filteredDataClassName = classNames(
        'custom-actions-modal__grid-panel d-flex justify-content-center gap-3 h-100 w-100',
        {
            'align-items-center': filteredData.length === 0,
            'custom-actions-modal__grid-panel--stacked': variant === 'all',
        },
    );

    return (
        <Modal show={show} className="custom-actions-modal w-100" centered onHide={onClose}>
            <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
                <AiOutlineClose onClick={onClose} className="close-button" data-testid="close-icon" />
            </Modal.Header>
            <Modal.Body className="p-3 d-flex flex-column gap-3 m-0 overflow-hidden">
                <SearchBar
                    onChange={onSearchQueryChange}
                    value={query}
                    searchClassName="search-container__input-cont"
                />
                <div className="d-flex gap-3 overflow-hidden custom-scrollbar h-100">
                    {variant === 'all' && (
                        <IntegrationsFilter
                            activeFilter={filter}
                            integrationsFilterData={filters}
                            onClickCategory={setFilter}
                        />
                    )}
                    <div className={filteredDataClassName}>
                        {filteredData.length === 0 ? (
                            <EmptySearchResults />
                        ) : (
                            <IntegrationsGrid
                                data={filteredData}
                                variant={variant}
                                handleCardClick={onCardClick}
                                onAmplifierGroupClick={() => setFilter(AMPLIFIER)}
                                onMicrosoftGroupClick={() => setFilter(MICROSOFT)}
                                serviceProfileData={serviceProfileData}
                                trainingTemplateDefinitions={trainingTemplateDefinitions}
                            />
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default CustomActionsModal;
